import { useCallback } from "react";

/**
 * Custom hook for handling paste events with image file support
 *
 * Extracts image files from clipboard paste events and delegates to
 * a file drop handler for upload processing.
 *
 * @param handleFileDrop - Function to handle the dropped/pasted files
 * @returns A paste event handler function
 *
 * @example
 * ```tsx
 * const { handleDrop } = useFileUpload({ onUpload });
 * const handlePaste = usePasteHandler(handleDrop);
 *
 * return <textarea onPaste={handlePaste} />;
 * ```
 */
export const usePasteHandler = (handleFileDrop: (files: File[]) => Promise<void>) => {
  return useCallback(
    async (e: React.ClipboardEvent) => {
      const clipboardFiles = e.clipboardData?.files;

      if (clipboardFiles && clipboardFiles.length > 0) {
        // Filter for image files only
        const imageFiles = Array.from(clipboardFiles).filter((file) =>
          file.type.startsWith("image/"),
        );

        if (imageFiles.length > 0) {
          e.preventDefault();
          await handleFileDrop(imageFiles);
        }
      }
    },
    [handleFileDrop],
  );
};
